/**
 * Builds the toast summary for a bulk approve/unapprove result.
 *
 * The endpoints return per-asset results; users need to see how many succeeded
 * and how many were skipped (e.g. not an assignee of the current step). We show
 * the dominant failure reason when everything that failed failed for the same
 * reason, otherwise a plain skipped count.
 */
import type { BulkApprovalResultDto } from '@inference.engine/workspace-api-client/web';

import { i18n } from '@/shared/i18n';

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

// BE reasons can embed raw internal ids (e.g. "Asset with ID <uuid> not found
// in project <uuid>"). Redact them before showing the reason to end users.
function sanitizeReason(reason: string): string {
  return reason.replace(UUID_RE, 'ID');
}

const HEAD_KEY = {
  approved: 'projects:bulkApproveAssets.summarizeResults.approvedHead',
  unapproved: 'projects:bulkApproveAssets.summarizeResults.unapprovedHead',
} as const;

export type BulkApprovalAction = keyof typeof HEAD_KEY;

export function summarizeBulkResults(
  results: BulkApprovalResultDto[],
  action: BulkApprovalAction,
): string {
  const succeeded = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);

  const head = i18n.t(HEAD_KEY[action], { count: succeeded.length });

  if (failed.length === 0) {
    return i18n.t('projects:bulkApproveAssets.summarizeResults.summary', { head });
  }

  // Compute distinctness over *all* failures, treating a missing reason as its
  // own value — otherwise `[{reason:'x'}, {reason:undefined}]` would collapse to
  // a single reason and mislabel every skip. Only surface the suffix when the
  // one shared reason is non-empty.
  const reasons = new Set(failed.map((r) => r.reason ?? ''));
  const sharedReason = reasons.size === 1 ? [...reasons][0] : '';
  const reasonSuffix = sharedReason
    ? i18n.t('projects:bulkApproveAssets.summarizeResults.withReason', {
        reason: sanitizeReason(sharedReason),
      })
    : '';
  return i18n.t('projects:bulkApproveAssets.summarizeResults.summaryWithSkipped', {
    head,
    skippedCount: failed.length,
    reason: reasonSuffix,
  });
}
